// Shared helper for checking whether a venue is closed for a date (or date
// range) due to an all-day closure recorded in venue_day_blocks (e.g. the
// hotel shutting for Christmas or the January off-season).
//
// This mirrors the guardrail restaurantConstants.js already gives the
// table-booking venues (Reynards/R&R/Brewery) — see getSittingsForVenueAndDate
// — but for the event/function-room venues (Skeffington Suites, Annalee,
// Corrick, New Bar, Foyer), which have no day-of-week schedule rules, only
// ad-hoc closures. Used by Events.jsx and RoomDiary.jsx's add/edit-event
// save paths to stop staff booking a function room over a closure.
//
// `blocks` is the array returned by supabaseService's
// fetchVenueBlocksByDates (shape: { venueId, date, sittingId, reason,
// blockType }).

import { toLocalDateKey, parseLocalDate, addDays } from './dateHelpers';

/** All date keys from start through end (inclusive). Falls back to just start if end is missing/invalid/before start. */
export function dateRangeKeys(startDateStr, endDateStr) {
  const start = parseLocalDate(startDateStr);
  if (!start) return [];
  const end = endDateStr ? parseLocalDate(endDateStr) : null;
  if (!end || end < start) return [toLocalDateKey(start)];
  const keys = [];
  let d = start;
  while (d <= end) {
    keys.push(toLocalDateKey(d));
    d = addDays(d, 1);
  }
  return keys;
}

/**
 * Returns the closure reason (a non-empty string) if `venueId` is closed
 * all-day on any date in [startDateStr, endDateStr], else null.
 * Only all_day 'closed' blocks count — per-sitting blocks are a
 * restaurant-only concept (breakfast/lunch/evening) and don't apply to
 * function-room events, which don't have sittings.
 */
export function getVenueClosureReason(venueId, startDateStr, endDateStr, blocks = []) {
  if (!venueId || !startDateStr) return null;
  const dates = new Set(dateRangeKeys(startDateStr, endDateStr));
  const hit = (blocks || []).find(
    (b) => b.venueId === venueId && b.sittingId === 'all_day' && b.blockType === 'closed' && dates.has(b.date)
  );
  return hit ? (hit.reason || 'Hotel closed') : null;
}
